import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { Database, FileCode2, SquareCode } from 'lucide-react';
import { toast } from 'sonner';

import { AssetContentTypeEnum, ContentTypeEnum } from 'src/graphql/schema';
import { AssetsUpdateDocument } from 'src/graphql/actions/asset/assetsUpdate.mutation';
import { SoftwareEscrowUpdateContentTypesDocument } from 'src/graphql/actions/softwareEscrow/softwareEscrowUpdateContentTypes.mutation';
import { ClientError, getApiError } from 'src/utils/errors';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { ToggleGroupCard, ToggleGroupCardItem } from '@/components/ui/toggle-group';
import { useAssetsContext, refetchAssets } from 'src/containers/Deposits';
import { useSoftwareEscrow } from 'src/context/software-escrow';

const options = [
  {
    name: 'Source code',
    description:
      'The core source code, including all scripts, libraries, and dependencies required to build and run the application.',
    value: AssetContentTypeEnum.SourceCode,
    icon: SquareCode,
  },
  {
    name: 'Documentation',
    description:
      'Technical and operational guides, including API references, setup instructions, system architecture, and any relevant notes required to transfer, operate, and maintain the application.',
    value: AssetContentTypeEnum.Documentation,
    icon: FileCode2,
  },
  {
    name: 'Data',
    description:
      "Essential datasets, configurations, and database exports necessary for the application's functionality.",
    value: AssetContentTypeEnum.Data,
    icon: Database,
  },
];

const RelabelDialog = () => {
  const {
    ui: {
      relabelDialog: { isOpen, assetIds },
    },
    closeRelabelDialog,
  } = useAssetsContext();
  const {
    softwareEscrow: { id: softwareEscrowId, requiredContentTypes },
  } = useSoftwareEscrow();
  const [updateSoftwareEscrow, { loading: loadingSoftwareEscrow }] = useMutation(
    SoftwareEscrowUpdateContentTypesDocument,
  );
  const [assetContentType, setAssetContentType] = useState<AssetContentTypeEnum | null>(null);
  const [updateAssets, { loading }] = useMutation(AssetsUpdateDocument, {
    onCompleted: refetchAssets,
  });
  const isMarkedAsRequired = assetContentType && (requiredContentTypes as string[]).includes(assetContentType);

  const onMarkRequired = async () => {
    if (isMarkedAsRequired) return;

    const newRequiredContentTypes = [...requiredContentTypes, assetContentType] as ContentTypeEnum[];

    await updateSoftwareEscrow({
      variables: {
        softwareEscrowId,
        requiredContentTypes: newRequiredContentTypes,
      },
    });
  };

  const isValid = assetIds.length && assetContentType !== null;

  const handleAssign = async () => {
    if (!isValid) return;

    try {
      // First mark the asset as required
      await onMarkRequired();

      // Then update the assets
      const { data } = await updateAssets({
        variables: {
          assetIds,
          assetParams: {
            contentType: assetContentType,
          },
        },
      });

      if (data?.assetsUpdate?.success) {
        toast.success('Asset relabeled!');
        closeRelabelDialog();
        setAssetContentType(null);
      } else {
        throw new ClientError(data?.assetsUpdate?.errors?.[0]);
      }
    } catch (error) {
      getApiError(error, toast.error);
    }
  };

  const onOpenChange = (open: boolean) => {
    if (!open) {
      closeRelabelDialog();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] gap-8">
        <DialogHeader className="gap-1.5">
          <DialogTitle className="text-xl font-bold">Relabel asset</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Choose which asset type this asset should be relabeled as.
          </DialogDescription>
        </DialogHeader>
        <ToggleGroupCard
          type="single"
          className="space-y-4"
          value={assetContentType ?? ''}
          onValueChange={(value) => setAssetContentType(value ? (value as AssetContentTypeEnum) : null)}
        >
          {options.map(({ value, icon, name, description }) => (
            <ToggleGroupCardItem key={value} value={value} icon={icon} title={name} description={description} />
          ))}
        </ToggleGroupCard>
        <DialogFooter className="sm:justify-between">
          <Button variant="outline" size="sm" onClick={closeRelabelDialog}>
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={!isValid || loadingSoftwareEscrow || loading}
            isLoading={loading || loadingSoftwareEscrow}
            onClick={handleAssign}
          >
            Relabel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RelabelDialog;
